<template>
  <ShallowCard
    :highlight="isUnread"
    class="relative"
    @click="removeUnreadHint"
  >
    <span
      v-if="isUnread"
      :class="$style.newBadge"
      class="mx-1.5 inline-block rounded-full bg-secondary px-[0.6em] py-[0.25em] text-[75%] font-bold leading-none text-center text-white align-baseline whitespace-nowrap"
    >Neu</span>
    <div class="flex flex-col justify-between">
      <div class="flex flex-row justify-between">
        <div class="text-left truncate">
          <span class="text-gray-800 font-semibold">
            {{ document.type?.name || 'Sonstiges' }}
          </span>
          <div
            class="truncate text-gray-700 text-sm"
            :title="fileName"
          >
            <FaIcon
              :name="document.extension === 'pdf' ? 'file-pdf' : 'file'"
              variant="light"
              class="mr-1.5"
            />
            {{ fileName }}
          </div>
        </div>
        <!-- Die Karte selbst öffnet auf Klick die Vorschau (`DocumentsView`). DsDropdown hielt den
             Klick auf seiner Schaltfläche mit `.stop` zurück, EP tut das nicht — der Tap auf das
             Menü landete deshalb in der Vorschau. Das `.stop` sitzt außen um EPs Auslöser herum,
             damit EPs eigener Handler innen noch feuert. Der Auslöser ist ein echter `<button>`,
             wie ihn DsDropdown selbst rendert: EPs `ElOnlyChild` verlangt genau ein Kind (Icon und
             `<label>` waren zwei), und ohne Schaltfläche ist das Menü weder per Tab erreichbar noch
             benannt — das `id`/`for`-Paar hing nach dem Umbau ins Leere, weil EP das `id` des
             Auslösers mit einem eigenen überschreibt (`aria-controls`-Verdrahtung). -->
        <div @click.stop>
          <ElDropdown
            trigger="click"
            placement="bottom-end"
            @visible-change="menuOpen = $event"
            @command="handleSelect"
          >
            <button
              type="button"
              aria-label="Menü"
              data-testid="document-card-menu"
              class="text-gray-700 hover:bg-gray-100 px-2 py-1.5 rounded-sm"
              :class="{'bg-gray-100': menuOpen}"
            >
              <FaIcon name="ellipsis" variant="regular" size="lg" />
            </button>

            <template #dropdown>
              <ElDropdownMenu class="el-dropdown-menu--plain w-[260px] py-2">
                <ElDropdownItem
                  v-for="(item, index) in dropdownItems"
                  :key="item.text"
                  :command="index"
                  :disabled="item.disabled"
                >
                  <div
                    class="flex flex-row items-center gap-4 py-2 px-5 text-gray-800 w-full"
                    :class="[{'opacity-50 cursor-not-allowed': item.disabled, 'hover:bg-gray-100 cursor-pointer': !item.disabled, [item.class]: !item.disabled}]"
                  >
                    <FaIcon
                      v-if="item.icon"
                      :name="item.icon"
                      variant="light"
                    />
                    {{ item.text }}
                  </div>
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>
      </div>
      <div class="mt-5">
        <div
          v-if="sparte !== null"
          class="my-1.5 text-sm"
        >
          {{ sparte }}
        </div>
        <div class="flex flex-row items-center gap-3">
          <CardGesellschaft
            v-if="document.company"
            :id="document.company.id"
            :name="document.company.name"
          />
          <div
            class="flex flex-row max-w-[50%] flex-1 items-center"
            @click.stop
          >
            <AppButton
              v-if="!contractNumber && isEditableByUser"
              type="primary"
              plain
              size="small"
              data-testid="associateContractButton"
              @click="showConnectContract = true"
            >
              Vertrag zuordnen
            </AppButton>
            <div
              v-else-if="routingContractId !== null"
              class="flex flex-row gap-1 max-w-full"
            >
              <RouterLink
                v-slot="{ navigate }"
                :to="{
                  name: Finanzmanager.VERTRAG,
                  params: { id: routingContractId }
                }"
                custom
              >
                <AppButton
                  type="primary"
                  text
                  :handler="navigate"
                  class="max-w-full"
                >
                  {{ contractNumber }}
                  <FaIcon name="external-link-alt" class="ml-1.5" />
                </AppButton>
              </RouterLink>
            </div>
          </div>
          <ConnectContractModal
            :show="showConnectContract"
            :document="document"
            :file-name="fileName"
            @close="showConnectContract = false"
            @open="showConnectContract = true"
          />
        </div>
      </div>
    </div>
    <ConfirmModal
      v-if="document.userDocumentId"
      v-model:show="showDeleteConfirm"
      title="Datei löschen"
      variant="danger"
      icon="trash"
      :confirm="() => deleteUserDocument(document.userDocumentId)"
    >
      Das Löschen der Datei kann nicht rückgängig gemacht werden. {{ duzen('Bist du', 'Sind Sie') }} sicher,
      dass {{ duzen('du fortfahren möchtest', 'Sie fortfahren möchten') }}?
    </ConfirmModal>
    <CardMeta>
      <div class="py-1.5 pl-2.5 flex grow justify-end text-xs md:text-sm">
        hochgeladen am: {{ formatDate(document.createdAt) }}
      </div>
    </CardMeta>
  </ShallowCard>
</template>

<script setup lang="ts">
import FaIcon from '@/application/components/ui/FaIcon.vue';
import AppButton from '@/application/components/ui/AppButton.vue';
import { captureException } from '@sentry/vue';
import { computed, ref } from 'vue';

import type { Document } from '@/api/documents/types';
import { UnreadHintType } from '@/api/unreadHint/types';
import { useUnreadHint } from '@/application/composables/unreadHint';
import CardGesellschaft from '@/contracts/components/CardGesellschaft.vue';
import CardMeta from '@/contracts/components/CardMeta.vue';
import ShallowCard from '@/contracts/components/ShallowCard.vue';
import ConnectContractModal from '@/documents/components/ConnectContractModal.vue';
import { useDocumentStore } from '@/documents/store/documentStore';
import { formatDate } from '@/application/utils/date';
import { spartenMapper } from '@/application/utils/sparteFormatter';
import { useUnreadHintStore } from '@/application/store/unreadHintStore';
import { call } from "@/api/lib/integration";
import { deleteDocument } from "@/api/documents/documentsApi";
import { useFileDownload } from "@/application/composables/fileDownload";
import { Finanzmanager } from "@/application/router/types/types";
import ConfirmModal from '@/application/components/ConfirmModal.vue';
import { ElDropdown, ElDropdownItem, ElDropdownMenu } from 'element-plus';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

export interface DropdownItem {
  icon: string,
  text: string,
  class?: string,
  function: () => void,
  disabled?: boolean,
}

interface Props {
  document: Document,
}

const props = defineProps<Props>();

const { isUnread } = useUnreadHint(UnreadHintType.Document, props.document.id);
const { removeUserDocument } = useDocumentStore();
const { downloadS3File } = useFileDownload();
const { duzen } = useBaseDataStore();

const menuOpen = ref(false);
const showDeleteConfirm = ref(false);
const showConnectContract = ref(false);

const sparte = computed(() => props.document.product ? spartenMapper(props.document.product) : null);
const fileName = computed(() => `${props.document.name}.${props.document.extension}`);

const deleteUserDocument = async (userDocumentId: number | null) => {
  if (userDocumentId === null) return;

  await call(
    deleteDocument({ userDocumentId }),
    () => {
      removeUserDocument(userDocumentId)
    },
    (error) => {
      captureException(error)
    },
  );
};

const contractNumber = computed(() => {
  const { contract } = props.document;
  if (!contract) return null;
  if (!contract.name) return 'Vertrag ohne Vertragsnr.';
  return contract.name;
});

const routingContractId = computed(() => {
  const { contract } = props.document;
  if (!contract) return null;
  return contract.id;
});

const isEditableByUser = computed(() => props.document.userDocumentId !== null);

const removeUnreadHint = async () => {
  await useUnreadHintStore().removeUnreadHints([{
    type: UnreadHintType.Document,
    object_id: Number(props.document.id)
  }], true)
}

const dropdownItems = computed((): DropdownItem[] => {
  const items: DropdownItem[] = [
    {
      icon: 'download',
      text: 'Herunterladen',
      function: () => downloadS3File(props.document.urls.download, fileName.value),
    },
    {
      icon: 'trash',
      text: 'Datei löschen',
      class: 'text-red-700',
      disabled: !props.document.canBeDeleted,
      function: () => {
        showDeleteConfirm.value = true
      }
    },
  ]

  if (props.document.contract) {
    items.splice(1, 0, {
      icon: 'pencil',
      text: 'Vertragszuordnung ändern',
      disabled: !isEditableByUser.value,
      function: () => {
        showConnectContract.value = true
      },
    })
  }

  if (isUnread.value) {
    items.splice(2, 0, {
      icon: 'envelope-open',
      text: 'Als gesehen markieren',
      function: () => removeUnreadHint(),
    })
  }
  return items;
})

function handleSelect(arrayIndex: number) {
  const item = dropdownItems.value[arrayIndex];
  if (item && !item.disabled) {
    item.function();
  }
}
</script>

<style module>
.newBadge {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(30%, -50%);
}
</style>
